import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  IconButton,
  Chip,
  Stack,
  Divider,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Circle as CircleIcon,
  CheckCircle as SuccessIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  useGetDeviceQuery,
  useUpdateDeviceMutation,
  useUpdateDeviceSecureCredentialsMutation,
  useLazyCheckSerialAvailabilityQuery,
  useLazyCheckCodeAvailabilityQuery,
} from '@store/api/deviceApi';
import { useGetLatestClientVersionQuery } from '@store/api/clientUpdatesApi';
import { useCreateDeploymentMutation } from '@store/api/deploymentsApi';
import DeviceVersionCard from './DeviceVersionCard';
import DeviceConnectionHistory from './DeviceConnectionHistory';
import InfoRow from '../common/InfoRow';
import { formatApn } from './lteApn';
import { fontFamilyMono } from '../../theme/theme';
import { useHasChanged } from '@hooks/useHasChanged';

interface DeviceOverviewProps {
  deviceId: string;
}

/** Save/cancel pair for the card's inline editors. `noun` names the field for
 *  screen readers, e.g. "location" -> "Save location". */
const EditActions: React.FC<{
  noun: string;
  onSave: () => void;
  onCancel: () => void;
  saveDisabled?: boolean;
}> = ({ noun, onSave, onCancel, saveDisabled }) => (
  <>
    <IconButton
      onClick={onSave}
      color="primary"
      size="small"
      aria-label={`Save ${noun}`}
      disabled={saveDisabled}
    >
      <SaveIcon fontSize="small" />
    </IconButton>
    <IconButton
      onClick={onCancel}
      color="secondary"
      size="small"
      aria-label={`Cancel editing ${noun}`}
    >
      <CancelIcon fontSize="small" />
    </IconButton>
  </>
);

const DeviceOverview: React.FC<DeviceOverviewProps> = ({ deviceId }) => {
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [isEditingSecureSerial, setIsEditingSecureSerial] = useState(false);
  const [isEditingSecureCode, setIsEditingSecureCode] = useState(false);
  const [tempName, setTempName] = useState('');
  const [tempCustomerName, setTempCustomerName] = useState('');
  const [tempLocation, setTempLocation] = useState('');
  const [tempSecureSerial, setTempSecureSerial] = useState<string>('');
  const [tempSecureCode, setTempSecureCode] = useState('');
  const [serialError, setSerialError] = useState<string>('');
  const [codeError, setCodeError] = useState<string>('');
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  // Fetch device data from Redux store
  const { data: device, isLoading, error } = useGetDeviceQuery(deviceId);
  const [updateDevice] = useUpdateDeviceMutation();
  const [updateSecureCredentials] = useUpdateDeviceSecureCredentialsMutation();
  const [checkSerialAvailability] = useLazyCheckSerialAvailabilityQuery();
  const [checkCodeAvailability] = useLazyCheckCodeAvailabilityQuery();
  const { data: latestClientVersion } = useGetLatestClientVersionQuery();
  const [createDeployment] = useCreateDeploymentMutation();
  const latestVersion = latestClientVersion?.version;

  // Initialize temp values when device data is loaded
  if (useHasChanged(device, undefined) && device) {
    setTempName(device.name);
    setTempCustomerName(device.customerName || '');
    setTempLocation(device.location || '');
    setTempSecureSerial(device.secureSerial ? String(device.secureSerial) : '');
    setTempSecureCode(device.secureCode || '');
  }

  const handleEditName = () => {
    setIsEditingName(true);
    setTempName(device?.name || '');
  };

  const handleSaveName = async () => {
    if (device && tempName !== device.name) {
      try {
        await updateDevice({ id: deviceId, data: { name: tempName } }).unwrap();
        setIsEditingName(false);
      } catch (error) {
        console.error('Failed to update device name:', error);
        // Reset to original name on error
        setTempName(device.name);
      }
    } else {
      setIsEditingName(false);
    }
  };

  const handleCancelEdit = () => {
    setTempName(device?.name || '');
    setIsEditingName(false);
  };

  const handleEditCustomer = () => {
    setIsEditingCustomer(true);
    setTempCustomerName(device?.customerName || '');
  };

  const handleSaveCustomer = async () => {
    if (device && tempCustomerName !== device.customerName) {
      try {
        await updateDevice({ id: deviceId, data: { customer_name: tempCustomerName } }).unwrap();
        setIsEditingCustomer(false);
      } catch (error) {
        console.error('Failed to update customer name:', error);
        setTempCustomerName(device.customerName || '');
      }
    } else {
      setIsEditingCustomer(false);
    }
  };

  const handleCancelEditCustomer = () => {
    setTempCustomerName(device?.customerName || '');
    setIsEditingCustomer(false);
  };

  const handleEditLocation = () => {
    setIsEditingLocation(true);
    setTempLocation(device?.location || '');
  };

  const handleSaveLocation = async () => {
    if (device && tempLocation !== device.location) {
      try {
        await updateDevice({ id: deviceId, data: { location: tempLocation } }).unwrap();
        setIsEditingLocation(false);
      } catch (error) {
        console.error('Failed to update location:', error);
        setTempLocation(device.location || '');
      }
    } else {
      setIsEditingLocation(false);
    }
  };

  const handleCancelEditLocation = () => {
    setTempLocation(device?.location || '');
    setIsEditingLocation(false);
  };

  const handleEditSecureSerial = () => {
    setIsEditingSecureSerial(true);
    setTempSecureSerial(device?.secureSerial ? String(device.secureSerial) : '');
    setSerialError('');
  };

  const handleSaveSecureSerial = async () => {
    if (!device) return;

    // Validate serial number
    const serialNumber = parseInt(tempSecureSerial, 10);
    if (isNaN(serialNumber) || serialNumber < 1 || serialNumber > 9999999) {
      setSerialError('Serial must be a number between 1 and 9999999');
      return;
    }

    // Check if serial is available (if changed)
    if (serialNumber !== parseInt(device.secureSerial || '0', 10)) {
      try {
        const result = await checkSerialAvailability({
          serial: serialNumber,
          excludeDeviceId: deviceId,
        }).unwrap();

        if (!result.available) {
          setSerialError(result.message);
          return;
        }

        // Update the secure serial
        await updateSecureCredentials({
          deviceId,
          secure_serial: serialNumber,
        }).unwrap();

        setIsEditingSecureSerial(false);
        setSerialError('');
      } catch (error: any) {
        console.error('Failed to update secure serial:', error);
        setSerialError(error?.data?.detail || 'Failed to update secure serial');
      }
    } else {
      setIsEditingSecureSerial(false);
    }
  };

  const handleCancelEditSecureSerial = () => {
    setTempSecureSerial(device?.secureSerial ? String(device.secureSerial) : '');
    setIsEditingSecureSerial(false);
    setSerialError('');
  };

  const handleEditSecureCode = () => {
    setIsEditingSecureCode(true);
    setTempSecureCode(device?.secureCode || '');
    setCodeError('');
  };

  const handleSaveSecureCode = async () => {
    if (!device) return;

    // Validate code format (4-7 characters alphanumeric)
    if (tempSecureCode.length < 4 || tempSecureCode.length > 7) {
      setCodeError('Code must be 4-7 characters long');
      return;
    }

    if (!/^[A-Za-z0-9]+$/.test(tempSecureCode)) {
      setCodeError('Code must be alphanumeric');
      return;
    }

    // Check if code is available (if changed)
    if (tempSecureCode !== device.secureCode) {
      try {
        const result = await checkCodeAvailability({
          code: tempSecureCode.toUpperCase(),
          excludeDeviceId: deviceId,
        }).unwrap();

        if (!result.available) {
          setCodeError(result.message);
          return;
        }

        // Update the secure code
        await updateSecureCredentials({
          deviceId,
          secure_code: tempSecureCode.toUpperCase(),
        }).unwrap();

        setIsEditingSecureCode(false);
        setCodeError('');
      } catch (error: any) {
        console.error('Failed to update secure code:', error);
        setCodeError(error?.data?.detail || 'Failed to update secure code');
      }
    } else {
      setIsEditingSecureCode(false);
    }
  };

  const handleCancelEditSecureCode = () => {
    setTempSecureCode(device?.secureCode || '');
    setIsEditingSecureCode(false);
    setCodeError('');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      case 'unknown':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatLastSeen = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={120} sx={{ mb: 3 }} />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Skeleton variant="rectangular" height={200} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !device) {
    return (
      <Alert severity="error">
        Failed to load device information. Please try again later.
      </Alert>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Device Name and Status Card */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mb: 1
                }}>
                <Chip
                  icon={<CircleIcon style={{ fontSize: 12 }} />}
                  label={device.status.toUpperCase()}
                  color={getStatusColor(device.status)}
                  size="small"
                />
              </Box>
              {/* The name is a field here, not a heading — it used to restate the
                  page <h1> one size down. The card titles are the real h2s. */}
              <Stack spacing={1}>
                {isEditingName ? (
                  <InfoRow
                    label="Device Name"
                    value={
                      // `htmlInput`, not `input`: on TextField the `input` slot is
                      // the InputBase wrapper, so an aria-label there leaves the
                      // <input> itself nameless.
                      <TextField
                        value={tempName}
                        onChange={(e) => setTempName(e.target.value)}
                        size="small"
                        autoFocus
                        slotProps={{ htmlInput: { 'aria-label': 'Device name' } }}
                        error={!tempName.trim()}
                        helperText={!tempName.trim() ? 'Device name is required' : ''}
                      />
                    }
                    action={
                      <EditActions
                        noun="device name"
                        onSave={handleSaveName}
                        onCancel={handleCancelEdit}
                        saveDisabled={!tempName.trim()}
                      />
                    }
                  />
                ) : (
                  <InfoRow
                    label="Device Name"
                    value={device.name}
                    action={
                      <IconButton onClick={handleEditName} size="small" aria-label="Edit device name">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    }
                  />
                )}
                <InfoRow label="Device ID" value={deviceId} mono />
                <InfoRow label="Registration ID" value={device.registrationId} mono />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Version Information Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <DeviceVersionCard
            device={device}
            latestVersion={latestVersion}
            onUpdateClick={() => setUpdateDialogOpen(true)}
          />
        </Grid>

        {/* Device Information Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Device Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {/* Customer Name - Editable */}
                {isEditingCustomer ? (
                  <InfoRow
                    label="Customer"
                    value={
                      <TextField
                        value={tempCustomerName}
                        onChange={(e) => setTempCustomerName(e.target.value)}
                        size="small"
                        placeholder="Customer name"
                        slotProps={{ htmlInput: { 'aria-label': 'Customer name' } }}
                        sx={{ width: 200 }}
                      />
                    }
                    action={
                      <EditActions
                        noun="customer name"
                        onSave={handleSaveCustomer}
                        onCancel={handleCancelEditCustomer}
                      />
                    }
                  />
                ) : (
                  <InfoRow
                    label="Customer"
                    value={device.customerName || 'Not set'}
                    action={
                      <IconButton
                        onClick={handleEditCustomer}
                        size="small"
                        aria-label="Edit customer name"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    }
                  />
                )}

                {/* Location - Editable */}
                {isEditingLocation ? (
                  <InfoRow
                    label="Location"
                    value={
                      <TextField
                        value={tempLocation}
                        onChange={(e) => setTempLocation(e.target.value)}
                        size="small"
                        placeholder="e.g., Dortmund, Door 1 EG"
                        slotProps={{ htmlInput: { 'aria-label': 'Location' } }}
                        sx={{ width: 250 }}
                      />
                    }
                    action={
                      <EditActions
                        noun="location"
                        onSave={handleSaveLocation}
                        onCancel={handleCancelEditLocation}
                      />
                    }
                  />
                ) : (
                  <InfoRow
                    label="Location"
                    value={device.location || 'Not set'}
                    action={
                      <IconButton
                        onClick={handleEditLocation}
                        size="small"
                        aria-label="Edit location"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    }
                  />
                )}

                {device.hardware.model && (
                  <InfoRow label="Model" value={device.hardware.model} />
                )}
                {device.hardware.serialNumber && (
                  <InfoRow label="MAC-based Serial" value={device.hardware.serialNumber} mono />
                )}
                {device.hardware.firmware && (
                  <InfoRow label="Firmware Version" value={device.hardware.firmware} />
                )}
                {device.clientVersion && (
                  <InfoRow label="IoT Client Version" value={device.clientVersion} />
                )}
                {device.osVersion && (
                  <InfoRow label="Operating System" value={device.osVersion} />
                )}
                {device.comnovoGatewayVersion && (
                  <InfoRow label="Comnovo Gateway Version" value={device.comnovoGatewayVersion} />
                )}
                {device.dotnetRuntimeVersion && (
                  <InfoRow label=".NET Runtime" value={device.dotnetRuntimeVersion} />
                )}
                {device.osUpdatesPending != null && (
                  <InfoRow
                    label="OS Updates Pending"
                    value={`${device.osUpdatesPending}${
                      device.osUpdatesCheckedAt
                        ? ` (checked ${formatLastSeen(device.osUpdatesCheckedAt)})`
                        : ''
                    }`}
                    color={device.osUpdatesPending > 0 ? 'warning.main' : undefined}
                  />
                )}
                {device.rebootRequired && (
                  <InfoRow label="Reboot Required" value="Yes" color="warning.main" />
                )}
                <InfoRow
                  label="DPS Environment"
                  value={`${device.dpsEnvironment || 'Unknown'}${
                    device.dpsIdScope ? ` (${device.dpsIdScope})` : ''
                  }`}
                  color={
                    !device.dpsEnvironment || device.dpsEnvironment === 'Unknown'
                      ? 'warning.main'
                      : device.dpsEnvironment === 'Production'
                      ? 'success.main'
                      : device.dpsEnvironment === 'DFO'
                      ? 'secondary.main'
                      : 'info.main'
                  }
                />
                <InfoRow
                  label="GSM Modem"
                  value={
                    device.gsmDisabled == null
                      ? 'Unknown'
                      : device.gsmDisabled
                      ? 'Disabled (No-SIM Image)'
                      : 'Enabled (SIM Image)'
                  }
                  color={
                    device.gsmDisabled == null
                      ? 'text.secondary'
                      : device.gsmDisabled
                      ? 'error.main'
                      : 'success.main'
                  }
                />
                {device.groupName && <InfoRow label="Group" value={device.groupName} />}
                <InfoRow label="Created" value={formatLastSeen(device.createdAt)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Network Information Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Network Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {device.network.ipAddress && (
                  <InfoRow label="IP Address" value={device.network.ipAddress} mono />
                )}
                {device.network.macAddress && (
                  <InfoRow label="MAC Address" value={device.network.macAddress} mono />
                )}
                {device.network.connectionType && (
                  <InfoRow label="Connection Type" value={device.network.connectionType} />
                )}
                {device.modemImei && (
                  <InfoRow label="Modem IMEI" value={device.modemImei} mono />
                )}
                {device.modemIccid && (
                  <InfoRow label="SIM ICCID" value={device.modemIccid} mono />
                )}
                {device.lteApn != null && (
                  <InfoRow label="LTE APN" value={formatApn(device.lteApn)} mono={device.lteApn !== ''} />
                )}
                <InfoRow label="Last Seen" value={formatLastSeen(device.lastSeen)} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* System Metrics Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                System Metrics
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                {device.diskUsage !== undefined && (
                  <InfoRow label="Disk Usage" value={`${device.diskUsage}%`} />
                )}
                {device.totalDiskGb != null && (
                  <InfoRow
                    label="Total Disk Capacity"
                    value={`${device.totalDiskGb.toFixed(1)} GB`}
                  />
                )}
                {device.cpuUsage !== undefined && (
                  <InfoRow label="CPU Usage" value={`${device.cpuUsage}%`} />
                )}
                {device.memoryUsage !== undefined && (
                  <InfoRow label="Memory Usage" value={`${device.memoryUsage}%`} />
                )}
                {!device.diskUsage && !device.cpuUsage && !device.memoryUsage && (
                  <Typography variant="body2" sx={{
                    color: 'text.secondary'
                  }}>
                    No metrics available yet. Metrics will appear after the next heartbeat.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* TPM Information Card */}
        {device.tpm && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                  TPM Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  <InfoRow label="Manufacturer" value={device.tpm.manufacturer} />
                  <InfoRow label="Version" value={device.tpm.version} />
                  {/* "Configured" is a status word, not a key, so it is not
                      monospaced — the key material itself is never rendered. */}
                  <InfoRow
                    label="EK Public Key"
                    value={device.tpm.ekPublic ? 'Configured' : 'Not configured'}
                    color={device.tpm.ekPublic ? 'success.main' : 'text.secondary'}
                  />
                  <InfoRow
                    label="SRK Public Key"
                    value={device.tpm.srkPublic ? 'Configured' : 'Not configured'}
                    color={device.tpm.srkPublic ? 'success.main' : 'text.secondary'}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Security Information Card */}
        {(device.secureSerial || device.secureCode || device.macAddress) && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" component="h2" gutterBottom>
                  Security Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {device.macAddress && (
                    <InfoRow label="MAC Address" value={device.macAddress} mono />
                  )}

                  {/* Secure Serial - Editable */}
                  {isEditingSecureSerial ? (
                    <InfoRow
                      label="Secure Serial"
                      value={
                        <TextField
                          value={tempSecureSerial}
                          onChange={(e) => setTempSecureSerial(e.target.value)}
                          size="small"
                          placeholder="e.g., 1690005"
                          sx={{ width: 150 }}
                          error={!!serialError}
                          helperText={serialError}
                          slotProps={{
                            htmlInput: {
                              'aria-label': 'Secure serial',
                              style: { fontFamily: fontFamilyMono },
                            },
                          }}
                        />
                      }
                      action={
                        <EditActions
                          noun="secure serial"
                          onSave={handleSaveSecureSerial}
                          onCancel={handleCancelEditSecureSerial}
                        />
                      }
                    />
                  ) : (
                    <InfoRow
                      label="Secure Serial"
                      value={device.secureSerial || 'Not set'}
                      mono={!!device.secureSerial}
                      action={
                        <IconButton
                          onClick={handleEditSecureSerial}
                          size="small"
                          aria-label="Edit secure serial"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      }
                    />
                  )}

                  {/* Secure Code - Editable */}
                  {isEditingSecureCode ? (
                    <InfoRow
                      label="Secure Code"
                      value={
                        <TextField
                          value={tempSecureCode}
                          onChange={(e) => setTempSecureCode(e.target.value.toUpperCase())}
                          size="small"
                          placeholder="e.g., GTQ64JG"
                          sx={{ width: 150 }}
                          error={!!codeError}
                          helperText={codeError}
                          slotProps={{
                            htmlInput: {
                              'aria-label': 'Secure code',
                              style: { fontFamily: fontFamilyMono },
                              maxLength: 7,
                            },
                          }}
                        />
                      }
                      action={
                        <EditActions
                          noun="secure code"
                          onSave={handleSaveSecureCode}
                          onCancel={handleCancelEditSecureCode}
                        />
                      }
                    />
                  ) : (
                    <InfoRow
                      label="Secure Code"
                      value={device.secureCode || 'Not set'}
                      mono={!!device.secureCode}
                      action={
                        <IconButton
                          onClick={handleEditSecureCode}
                          size="small"
                          aria-label="Edit secure code"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      }
                    />
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid size={12}>
          <DeviceConnectionHistory deviceId={deviceId} />
        </Grid>
      </Grid>

      {/* Update Dialog */}
      <Dialog open={updateDialogOpen} onClose={() => !updating && setUpdateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Update Client to {latestVersion}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {updateError && (
              <Alert severity="error" onClose={() => setUpdateError(null)}>
                {updateError}
              </Alert>
            )}
            {updateSuccess && (
              <Alert severity="success" icon={<SuccessIcon />}>
                Update queued successfully! Follow its progress under Update History.
              </Alert>
            )}
            <Alert severity="info" icon={<InfoIcon />}>
              This will queue an update command for device <strong>{device?.name}</strong> to version <strong>{latestVersion}</strong>.
              The update will be securely downloaded and verified with digital signatures.
            </Alert>
            <Box>
              <Typography variant="body2" gutterBottom sx={{
                color: 'text.secondary'
              }}>
                Current Version: <strong>{device?.clientVersion || 'Unknown'}</strong>
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary'
              }}>
                Target Version: <strong>{latestVersion}</strong>
              </Typography>
            </Box>
            {updating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Queuing update command...</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialogOpen(false)} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (!latestVersion) {
                setUpdateError('No update version available');
                return;
              }

              try {
                setUpdating(true);
                setUpdateError(null);
                setUpdateSuccess(false);

                // A one-device Deployment, so it shows up under Update History.
                await createDeployment({
                  kind: 'client',
                  version: latestVersion,
                  device_ids: [deviceId],
                  options: { timeout: 360, rollback_on_failure: true },
                }).unwrap();

                setUpdateSuccess(true);
                setTimeout(() => {
                  setUpdateDialogOpen(false);
                  setUpdateSuccess(false);
                }, 2000);
              } catch (err: any) {
                setUpdateError(err.message || err.response?.data?.detail || 'Failed to queue update command');
              } finally {
                setUpdating(false);
              }
            }}
            variant="contained"
            color="primary"
            disabled={updating || updateSuccess}
          >
            {updating ? 'Updating...' : updateSuccess ? 'Command Queued!' : 'Update Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceOverview;
